/**
 * AgentEditForm — "Edit Agent" (F031, form-spec `FS-F031-AGENT`, A-02).
 *
 * Edits a registered agent's mutable attributes. `PATCH /api/portal/agents/{id}` — undefined
 * members leave the field unchanged (`UpdateAgentRequest`). Per C-02, Agent Name and Owner Id are
 * NOT part of `UpdateAgentRequest` — they are immutable on edit and rendered read-only for context
 * rather than as editable controls (ADR-013: a distinct form type from `AgentRegisterForm`, its
 * own submit path).
 *
 * Pattern: `react-hook-form` + `zodResolver`, schema imported from `AgentEdit.schema.ts` (never
 * redefined), Fluent `Field` for verbatim `V-` messages, server errors mapped via the shared
 * `applyServerErrors` (Charter M3 / form-standard.context.md). The schema validates the full
 * editable-field snapshot; the sparse `UpdateAgentRequest` PATCH body (undefined = unchanged) is
 * built here by diffing the submitted values against the loaded snapshot — see `AgentEdit.schema.ts`
 * for the `metadataJson` special case. The outer `Dialog` chrome is owned by `AgentRegistryPage.tsx`
 * (`src/pages/**`) — this component renders the form CONTENT only and keeps its existing
 * `onSuccess`/`onCancel`/`onDirtyChange` prop contract so the hosting page needs no change.
 */
import { useEffect, useRef, useState } from 'react';
import type { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Spinner,
  Switch,
  Text,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { SaveRegular } from '@fluentui/react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agentsApi } from '../../../services/api';
import type { Agent, AgentEnvironment, DataAccessLevel, ToolAccessLevel, UpdateAgentRequest } from '../../../types';
import { applyServerErrors, useReportFormDirty, FormActions, ServerErrorBanner } from '../../../components/forms';
import { AGENT_ENVIRONMENT_OPTIONS, DATA_ACCESS_LEVEL_OPTIONS, TOOL_ACCESS_LEVEL_OPTIONS } from './agentFormFields';
import { AgentEditSchema, type AgentEditFormValues, type AgentEditFormOutput } from './AgentEdit.schema';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  readOnlyRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXXL,
  },
});

// Known request-DTO field keys — used by the server-error -> field mapping (B-03/A-02).
const KNOWN_FIELDS = [
  'purpose',
  'ownerTeam',
  'environment',
  'modelProvider',
  'modelName',
  'runtimeProvider',
  'dataAccessLevel',
  'toolAccessLevel',
  'mcpEnabled',
  'agentVersion',
  'metadataJson',
] as const;

const DEFAULT_VALUES: AgentEditFormValues = {
  agentVersion: '',
  purpose: '',
  ownerTeam: '',
  environment: 'Development',
  modelProvider: '',
  modelName: '',
  runtimeProvider: '',
  dataAccessLevel: 'None',
  toolAccessLevel: 'None',
  mcpEnabled: false,
  metadataJson: '',
};

/** The loaded snapshot, normalised to the same shape `AgentEditSchema`'s output produces, so the
 *  submit handler can diff post-transform values against it field-for-field (A-02: undefined =
 *  unchanged). `metadataJson` is deliberately absent — see `AgentEdit.schema.ts`. */
interface EditableSnapshot {
  agentVersion: string;
  purpose: string;
  ownerTeam: string | undefined;
  environment: AgentEnvironment;
  modelProvider: string;
  modelName: string;
  runtimeProvider: string | undefined;
  dataAccessLevel: DataAccessLevel;
  toolAccessLevel: ToolAccessLevel;
  mcpEnabled: boolean;
}

export interface AgentEditFormProps {
  agentId: number;
  onSuccess?: (agent: Agent) => void;
  onCancel?: () => void;
  /** B-04 — reports dirty state up so the hosting dialog can guard an unsaved-changes dismiss. */
  onDirtyChange?: (dirty: boolean) => void;
}

export const AgentEditForm: FC<AgentEditFormProps> = ({ agentId, onSuccess, onCancel, onDirtyChange }) => {
  const styles = useStyles();
  const queryClient = useQueryClient();

  // B-01 — edit: load agent via agentsApi.getById(id) -> map to the editable subset.
  const { data: agentDetail, isLoading, isError, refetch } = useQuery({
    queryKey: ['agent-detail', agentId],
    queryFn: () => agentsApi.getById(agentId),
  });

  const {
    control,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<AgentEditFormValues, unknown, AgentEditFormOutput>({
    resolver: zodResolver(AgentEditSchema),
    mode: 'onTouched',
    defaultValues: DEFAULT_VALUES,
  });

  useReportFormDirty(isDirty, onDirtyChange);

  const environment = watch('environment');

  const initializedRef = useRef(false);
  const initialRef = useRef<EditableSnapshot | null>(null);

  useEffect(() => {
    if (agentDetail && !initializedRef.current) {
      const snapshot: EditableSnapshot = {
        agentVersion: agentDetail.agent.agentVersion,
        purpose: agentDetail.agent.purpose,
        ownerTeam: agentDetail.agent.ownerTeam || undefined,
        environment: agentDetail.agent.environment,
        modelProvider: agentDetail.agent.modelProvider,
        modelName: agentDetail.agent.modelName,
        runtimeProvider: agentDetail.agent.runtimeProvider || undefined,
        dataAccessLevel: agentDetail.agent.dataAccessLevel,
        toolAccessLevel: agentDetail.agent.toolAccessLevel,
        mcpEnabled: agentDetail.agent.mcpEnabled,
      };
      // No source field to prefill Metadata from (AgentDetailDto doesn't expose it) — starts
      // blank; left blank means "unchanged" (undefined on PATCH), matching `UpdateAgentRequest`
      // semantics (AgentEdit.schema.ts).
      reset({
        agentVersion: snapshot.agentVersion,
        purpose: snapshot.purpose,
        ownerTeam: snapshot.ownerTeam ?? '',
        environment: snapshot.environment,
        modelProvider: snapshot.modelProvider,
        modelName: snapshot.modelName,
        runtimeProvider: snapshot.runtimeProvider ?? '',
        dataAccessLevel: snapshot.dataAccessLevel,
        toolAccessLevel: snapshot.toolAccessLevel,
        mcpEnabled: snapshot.mcpEnabled,
        metadataJson: '',
      });
      initialRef.current = snapshot;
      initializedRef.current = true;
    }
  }, [agentDetail, reset]);

  const [globalError, setGlobalError] = useState<string | undefined>();

  const updateMutation = useMutation({
    mutationFn: (patch: UpdateAgentRequest) => agentsApi.update(agentId, patch),
    onSuccess: (agent) => {
      queryClient.invalidateQueries({ queryKey: ['agent-detail', agentId] });
      onSuccess?.(agent);
    },
    onError: (error) => {
      const { globalMessage } = applyServerErrors(error, KNOWN_FIELDS, setError);
      setGlobalError(globalMessage);
    },
  });

  const onValid = (values: AgentEditFormOutput) => {
    // B-02 — guard double-submit.
    if (updateMutation.isPending || !initialRef.current) return;
    setGlobalError(undefined);

    const initial = initialRef.current;
    const patch: UpdateAgentRequest = {
      purpose: values.purpose !== initial.purpose ? values.purpose : undefined,
      ownerTeam: values.ownerTeam !== initial.ownerTeam ? values.ownerTeam : undefined,
      environment: values.environment !== initial.environment ? values.environment : undefined,
      modelProvider: values.modelProvider !== initial.modelProvider ? values.modelProvider : undefined,
      modelName: values.modelName !== initial.modelName ? values.modelName : undefined,
      runtimeProvider: values.runtimeProvider !== initial.runtimeProvider ? values.runtimeProvider : undefined,
      dataAccessLevel: values.dataAccessLevel !== initial.dataAccessLevel ? values.dataAccessLevel : undefined,
      toolAccessLevel: values.toolAccessLevel !== initial.toolAccessLevel ? values.toolAccessLevel : undefined,
      mcpEnabled: values.mcpEnabled !== initial.mcpEnabled ? values.mcpEnabled : undefined,
      agentVersion: values.agentVersion !== initial.agentVersion ? values.agentVersion : undefined,
      // metadataJson is never diffed — blank always means "unchanged" (AgentEdit.schema.ts).
      metadataJson: values.metadataJson,
    };

    updateMutation.mutate(patch);
  };

  // B-05 — loading / error states for the edit prefill.
  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner label="Loading agent…" />
      </div>
    );
  }

  if (isError || !agentDetail) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>Unable to Load Agent</MessageBarTitle>
          The agent's details could not be loaded.
          <Button appearance="transparent" size="small" onClick={() => refetch()}>
            Try Again
          </Button>
        </MessageBarBody>
      </MessageBar>
    );
  }

  return (
    // `noValidate` — see AgentRegisterForm.tsx for why.
    <form onSubmit={handleSubmit(onValid)} className={styles.form} noValidate>
      <ServerErrorBanner title="Unable to Update Agent" message={globalError} testId="agent-edit-form-server-error" />

      {/* C-01 — Environment = Production advisory. */}
      {environment === 'Production' && (
        <ServerErrorBanner
          title="Production Placement"
          message="Registering computes risk and enforces the production-placement guard."
          testId="agent-edit-form-production-warning"
          intent="warning"
        />
      )}

      {/* C-02 — Agent Name + Owner Id are not on UpdateAgentRequest; read-only for context. */}
      <div className={styles.readOnlyRow}>
        <Text size={200} weight="semibold">Agent Name</Text>
        <Text>{agentDetail.agent.agentName}</Text>
      </div>
      <div className={styles.readOnlyRow}>
        <Text size={200} weight="semibold">Owner Id</Text>
        <Text>{agentDetail.agent.ownerId}</Text>
      </div>

      <Field
        label="Agent Version"
        required
        validationState={errors.agentVersion ? 'error' : undefined}
        validationMessage={errors.agentVersion?.message}
      >
        <Controller
          name="agentVersion"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="agent-edit-form-agentVersion"
              value={field.value ?? ''}
              placeholder="e.g. 1.4.0"
              disabled={updateMutation.isPending}
              maxLength={50}
            />
          )}
        />
      </Field>

      <Field
        label="Purpose"
        required
        validationState={errors.purpose ? 'error' : undefined}
        validationMessage={errors.purpose?.message}
      >
        <Controller
          name="purpose"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              data-testid="agent-edit-form-purpose"
              value={field.value ?? ''}
              placeholder="What this agent does and why"
              disabled={updateMutation.isPending}
              resize="vertical"
            />
          )}
        />
      </Field>

      <Field
        label="Owner Team"
        validationState={errors.ownerTeam ? 'error' : undefined}
        validationMessage={errors.ownerTeam?.message}
      >
        <Controller
          name="ownerTeam"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="agent-edit-form-ownerTeam"
              value={field.value ?? ''}
              placeholder="Owning team (optional)"
              disabled={updateMutation.isPending}
              maxLength={200}
            />
          )}
        />
      </Field>

      <Field
        label="Environment"
        required
        validationState={errors.environment ? 'error' : undefined}
        validationMessage={errors.environment?.message}
      >
        <Controller
          name="environment"
          control={control}
          render={({ field }) => (
            <Dropdown
              value={field.value}
              selectedOptions={[field.value]}
              disabled={updateMutation.isPending}
              onOptionSelect={(_, data) => field.onChange(data.optionValue ?? field.value)}
              onBlur={field.onBlur}
              data-testid="agent-edit-form-environment"
            >
              {AGENT_ENVIRONMENT_OPTIONS.map((v) => (
                <Option key={v} value={v}>
                  {v}
                </Option>
              ))}
            </Dropdown>
          )}
        />
      </Field>

      <Field
        label="Model Provider"
        required
        validationState={errors.modelProvider ? 'error' : undefined}
        validationMessage={errors.modelProvider?.message}
      >
        <Controller
          name="modelProvider"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="agent-edit-form-modelProvider"
              value={field.value ?? ''}
              placeholder="e.g. Anthropic, Azure OpenAI"
              disabled={updateMutation.isPending}
              maxLength={100}
            />
          )}
        />
      </Field>

      <Field
        label="Model Name"
        required
        validationState={errors.modelName ? 'error' : undefined}
        validationMessage={errors.modelName?.message}
      >
        <Controller
          name="modelName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="agent-edit-form-modelName"
              value={field.value ?? ''}
              placeholder="e.g. claude-opus-4-8"
              disabled={updateMutation.isPending}
              maxLength={200}
            />
          )}
        />
      </Field>

      <Field
        label="Runtime Provider"
        validationState={errors.runtimeProvider ? 'error' : undefined}
        validationMessage={errors.runtimeProvider?.message}
      >
        <Controller
          name="runtimeProvider"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="agent-edit-form-runtimeProvider"
              value={field.value ?? ''}
              placeholder="Hosting runtime (optional)"
              disabled={updateMutation.isPending}
              maxLength={100}
            />
          )}
        />
      </Field>

      <Field
        label="Data Access Level"
        required
        validationState={errors.dataAccessLevel ? 'error' : undefined}
        validationMessage={errors.dataAccessLevel?.message}
      >
        <Controller
          name="dataAccessLevel"
          control={control}
          render={({ field }) => (
            <Dropdown
              value={field.value}
              selectedOptions={[field.value]}
              disabled={updateMutation.isPending}
              onOptionSelect={(_, data) => field.onChange(data.optionValue ?? field.value)}
              onBlur={field.onBlur}
              data-testid="agent-edit-form-dataAccessLevel"
            >
              {DATA_ACCESS_LEVEL_OPTIONS.map((v) => (
                <Option key={v} value={v}>
                  {v}
                </Option>
              ))}
            </Dropdown>
          )}
        />
      </Field>

      <Field
        label="Tool Access Level"
        required
        validationState={errors.toolAccessLevel ? 'error' : undefined}
        validationMessage={errors.toolAccessLevel?.message}
      >
        <Controller
          name="toolAccessLevel"
          control={control}
          render={({ field }) => (
            <Dropdown
              value={field.value}
              selectedOptions={[field.value]}
              disabled={updateMutation.isPending}
              onOptionSelect={(_, data) => field.onChange(data.optionValue ?? field.value)}
              onBlur={field.onBlur}
              data-testid="agent-edit-form-toolAccessLevel"
            >
              {TOOL_ACCESS_LEVEL_OPTIONS.map((v) => (
                <Option key={v} value={v}>
                  {v}
                </Option>
              ))}
            </Dropdown>
          )}
        />
      </Field>

      <Field label="MCP Enabled" hint="Agent integrates via MCP">
        <Controller
          name="mcpEnabled"
          control={control}
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={(_, data) => field.onChange(data.checked)}
              disabled={updateMutation.isPending}
              label={field.value ? 'Enabled' : 'Disabled'}
              data-testid="agent-edit-form-mcpEnabled"
            />
          )}
        />
      </Field>

      <Field
        label="Metadata (JSON)"
        hint="Leave blank to keep the existing metadata unchanged."
        validationState={errors.metadataJson ? 'error' : undefined}
        validationMessage={errors.metadataJson?.message}
      >
        <Controller
          name="metadataJson"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              data-testid="agent-edit-form-metadataJson"
              value={field.value ?? ''}
              placeholder="Optional JSON metadata"
              disabled={updateMutation.isPending}
              resize="vertical"
            />
          )}
        />
      </Field>

      <FormActions
        mode="edit"
        testIdPrefix="agent-edit-form"
        isSubmitting={updateMutation.isPending}
        onCancel={onCancel}
        submitLabel="Save Changes"
        submittingLabel="Saving…"
        submitIcon={<SaveRegular />}
      />
    </form>
  );
};
